<script setup>
import { ref } from 'vue';
import CopilotEditor from 'dashboard/components/widgets/WootWriter/CopilotEditor.vue';

defineProps({
  showCopilotEditor: {
    type: Boolean,
    default: false,
  },
  isGeneratingContent: {
    type: Boolean,
    default: false,
  },
  generatedContent: {
    type: String,
    default: '',
  },
  areActionsDisabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'focus',
  'blur',
  'clearSelection',
  'contentReady',
  'send',
  'close',
  'accept',
  'discard',
]);

const copilotEditorContent = ref('');
const processingLogo = '/brand-assets/jarvis-thinking-logo.png';

const onFocus = () => {
  emit('focus');
};

const onBlur = () => {
  emit('blur');
};

const clearEditorSelection = () => {
  emit('clearSelection');
};

const onSend = () => {
  emit('send', copilotEditorContent.value);
  copilotEditorContent.value = '';
};
</script>

<template>
  <Transition
    mode="out-in"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    @after-enter="emit('contentReady')"
  >
    <CopilotEditor
      v-if="showCopilotEditor && !isGeneratingContent"
      key="copilot-editor"
      v-model="copilotEditorContent"
      class="copilot-editor"
      :generated-content="generatedContent"
      :actions-disabled="areActionsDisabled"
      :placeholder="placeholder"
      :min-height="4"
      :enabled-menu-options="[]"
      @focus="onFocus"
      @blur="onBlur"
      @clear-selection="clearEditorSelection"
      @send="onSend"
      @close="emit('close')"
      @accept="emit('accept')"
      @discard="emit('discard')"
    />
    <div
      v-else-if="isGeneratingContent"
      key="loading-state"
      class="jarvis-processing flex min-h-14 w-full items-center gap-2.5 rounded-full bg-[#e1f8ed] px-3 py-2.5 text-sm text-[#39cc87] shadow-sm md:min-h-16 md:gap-3 md:px-4"
      role="status"
      aria-live="polite"
    >
      <img
        :src="processingLogo"
        alt=""
        class="size-10 shrink-0 rounded-full object-cover"
      />
      <span class="whitespace-nowrap font-semibold">
        {{ $t('CONVERSATION.REPLYBOX.JARVIS_THINKING_NAME') }}
      </span>
      <span class="-ml-1 whitespace-nowrap">
        {{ $t('CONVERSATION.REPLYBOX.JARVIS_THINKING_STATUS') }}
      </span>
      <span class="ml-0.5 inline-flex items-center gap-1" aria-hidden="true">
        <i
          class="size-1.5 animate-pulse rounded-full bg-current motion-reduce:animate-none"
        />
        <i
          class="size-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms] motion-reduce:animate-none"
        />
        <i
          class="size-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms] motion-reduce:animate-none"
        />
      </span>
    </div>
  </Transition>
</template>

<style lang="scss">
.copilot-editor {
  .ProseMirror-menubar {
    display: none;
  }
}
</style>
